import * as React from 'react'

type Theme = 'light' | 'dark' | 'system'

const KEY = 'sbt.theme'

const ThemeContext = React.createContext<{
  theme: Theme
  resolved: 'light' | 'dark'
  setTheme: (theme: Theme) => void
}>({ theme: 'system', resolved: 'light', setTheme: () => {} })

/**
 * design.md §7: honour prefers-color-scheme, but let an explicit choice win and
 * persist. The class goes on <html> so the token overrides in styles.css apply.
 */
export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = React.useState<Theme>(
    () => (localStorage.getItem(KEY) as Theme | null) ?? 'system',
  )
  const [systemDark, setSystemDark] = React.useState(
    () => window.matchMedia('(prefers-color-scheme: dark)').matches,
  )

  React.useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = (event: MediaQueryListEvent) => setSystemDark(event.matches)
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [])

  const resolved: 'light' | 'dark' =
    theme === 'system' ? (systemDark ? 'dark' : 'light') : theme

  React.useEffect(() => {
    document.documentElement.classList.toggle('dark', resolved === 'dark')
  }, [resolved])

  const setTheme = React.useCallback((next: Theme) => {
    localStorage.setItem(KEY, next)
    setThemeState(next)
  }, [])

  const value = React.useMemo(() => ({ theme, resolved, setTheme }), [theme, resolved, setTheme])
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme() {
  return React.useContext(ThemeContext)
}
